import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Search, FileText, Tag, Calendar, DollarSign, AlertTriangle, Download } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { documentsApi, annotationsApi, handleApiError } from '@/services/api';
import { AnnotationCard } from '@/components/annotations/AnnotationCard';
import { SummaryViewer } from '@/components/annotations/SummaryViewer';
import { toast } from 'sonner';
import type { Document, Annotation } from '@/types';

export default function DocumentAnalysis() {
  const { projectId } = useParams<{ projectId: string }>();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAnnotated, setShowAnnotated] = useState(true);
  const [showPending, setShowPending] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (projectId) {
      loadDocuments();
    }
  }, [projectId]);

  useEffect(() => {
    if (selectedDoc) {
      loadAnnotations(selectedDoc.id);
    }
  }, [selectedDoc]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await documentsApi.list(Number(projectId));
      setDocuments(data);
      if (data.length > 0) {
        setSelectedDoc(data[0]);
      }
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const loadAnnotations = async (documentId: number) => {
    try {
      const data = await annotationsApi.list(documentId);
      setAnnotations(data);
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const filteredDocs = documents.filter((doc) => {
    const matchesSearch = doc.filename.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter =
      (showAnnotated && doc.processed) || (showPending && !doc.processed);
    return matchesSearch && matchesFilter;
  });

  // Group annotations by type
  const groupedAnnotations = annotations.reduce((acc, ann) => {
    const type = ann.annotation_type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(ann);
    return acc;
  }, {} as Record<string, Annotation[]>);

  const hasSummary = groupedAnnotations.event?.some(
    (ann) => ann.annotation_metadata?.is_summary
  );

  const summaryAnnotations = annotations.filter(
    (ann) => ann.annotation_metadata?.is_summary
  );

  const exportAnnotations = () => {
    if (!selectedDoc || annotations.length === 0) {
      toast.error('No annotations to export');
      return;
    }

    const data = {
      document: selectedDoc.filename,
      annotations: annotations.map((ann) => ({
        type: ann.annotation_type,
        content: ann.content,
        context: ann.context,
        page: ann.page_number,
        confidence: ann.confidence,
        metadata: ann.annotation_metadata,
      })),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedDoc.filename}.annotations.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Annotations exported');
  };

  return (
    <div className="flex h-[calc(100vh-theme(spacing.16))]">
      {/* Left Sidebar - Document List */}
      <div className="w-80 border-r bg-muted/10 flex flex-col">
        <div className="p-4 border-b space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="annotated"
                checked={showAnnotated}
                onCheckedChange={(checked) => setShowAnnotated(checked as boolean)}
              />
              <label htmlFor="annotated" className="text-sm cursor-pointer">
                Annotated ({documents.filter((d) => d.processed).length})
              </label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="pending"
                checked={showPending}
                onCheckedChange={(checked) => setShowPending(checked as boolean)}
              />
              <label htmlFor="pending" className="text-sm cursor-pointer">
                Pending ({documents.filter((d) => !d.processed).length})
              </label>
            </div>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-4 space-y-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : filteredDocs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No documents found</p>
              </div>
            ) : (
              filteredDocs.map((doc) => (
                <Card
                  key={doc.id}
                  className={`cursor-pointer transition-colors hover:bg-accent ${
                    selectedDoc?.id === doc.id ? 'border-primary bg-primary/5' : ''
                  }`}
                  onClick={() => setSelectedDoc(doc)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-2">
                      <FileText className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate text-sm">{doc.filename}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {doc.processed ? (
                            <span className="text-green-600 dark:text-green-400">
                              {doc.annotation_count || 0} annotations
                            </span>
                          ) : (
                            <span className="text-orange-600 dark:text-orange-400">Pending</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {selectedDoc ? (
          <>
            {/* Document Info Header */}
            <div className="border-b p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">{selectedDoc.filename}</h2>
                <p className="text-sm text-muted-foreground">
                  {selectedDoc.file_type.toUpperCase()} · {(selectedDoc.file_size / 1024).toFixed(1)} KB
                </p>
              </div>
              <Button onClick={exportAnnotations} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export Annotations
              </Button>
            </div>

            {/* Document Content */}
            <div className="h-1/2 border-b p-4 bg-muted/5">
              {selectedDoc.content ? (
                <ScrollArea className="h-full">
                  <div className="prose prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap text-sm">{selectedDoc.content}</pre>
                  </div>
                </ScrollArea>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Document content not available</p>
                    <p className="text-xs mt-1">Upload a document to view its content</p>
                  </div>
                </div>
              )}
            </div>

            {/* Annotations Area */}
            <div className="h-1/2 flex flex-col">
              <Tabs defaultValue={hasSummary ? 'summary' : 'all'} className="flex-1 flex flex-col">
                <div className="border-b px-4">
                  <TabsList>
                    {hasSummary && <TabsTrigger value="summary">Summary</TabsTrigger>}
                    <TabsTrigger value="all">All ({annotations.length})</TabsTrigger>
                    <TabsTrigger value="entity">
                      <Tag className="h-4 w-4 mr-1" />
                      Entities ({groupedAnnotations.entity?.length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="date">
                      <Calendar className="h-4 w-4 mr-1" />
                      Dates ({groupedAnnotations.date?.length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="amount">
                      <DollarSign className="h-4 w-4 mr-1" />
                      Amounts ({groupedAnnotations.amount?.length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="flag">
                      <AlertTriangle className="h-4 w-4 mr-1" />
                      Flags ({groupedAnnotations.flag?.length || 0})
                    </TabsTrigger>
                  </TabsList>
                </div>

                <ScrollArea className="flex-1">
                  <div className="p-4">
                    {hasSummary && (
                      <TabsContent value="summary" className="mt-0">
                        {summaryAnnotations.map((summary) => (
                          <SummaryViewer key={summary.id} summary={summary} />
                        ))}
                      </TabsContent>
                    )}

                    <TabsContent value="all" className="mt-0">
                      {annotations.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <p className="text-sm">No annotations yet</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {annotations.map((ann) => (
                            <AnnotationCard key={ann.id} annotation={ann} />
                          ))}
                        </div>
                      )}
                    </TabsContent>

                    {(['entity', 'date', 'amount', 'quote', 'event', 'flag'] as const).map((type) => (
                      <TabsContent key={type} value={type} className="mt-0">
                        {!groupedAnnotations[type] || groupedAnnotations[type].length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <p className="text-sm">No {type} annotations</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {groupedAnnotations[type].map((ann) => (
                              <AnnotationCard key={ann.id} annotation={ann} />
                            ))}
                          </div>
                        )}
                      </TabsContent>
                    ))}
                  </div>
                </ScrollArea>
              </Tabs>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No document selected</p>
              <p className="text-sm">Select a document from the list to view its annotations</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
